import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import EditIcon from "@mui/icons-material/Edit";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import axiosConfig from "../../../axios/axiosConfig";

const CreateScreenName = () => {
  const [screenLoading, setScreenLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [screenName, setScreenName] = useState("");
  const [screenList, setScreenList] = useState([]);
  const [isActive, setIsActive] = useState(true);

  const [editId, setEditId] = useState(null);
  const fetchScreens = async () => {
    setScreenLoading(true);
    try {
      const response = await axiosConfig({
        method: "get",
        url: "/appdashboard/screens",
      });

      const data = response?.data?.response || [];
      console.log("Fetched screens", data);
      setScreenList(data);
    } catch (error) {
      console.error("Error fetching screens:", error);
      toast.error(error?.response?.data?.response || "Failed to fetch screens");
    } finally {
      setScreenLoading(false);
    }
  };
  useEffect(() => {
    fetchScreens();
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!screenName.trim()) {
      toast.error("Screen name is required");
      return;
    }

    setSubmitLoading(true);

    try {
      if (editId) {
        await axiosConfig({
          method: "put",
          url: `/appdashboard/screens/${editId}/`,
          data: {
            screen_name: screenName,
            is_active: isActive,
          },
        });
        toast.success("Screen updated successfully");
      } else {
        await axiosConfig({
          method: "post",
          url: `/appdashboard/screens/`,
          data: {
            screen_name: screenName,
          },
        });
        toast.success("Screen created successfully");
      }

      setScreenName("");
      setEditId(null);
      setIsActive(true);
      fetchScreens();
    } catch (error) {
      console.error("❌ Error submitting screen:", error);
      toast.error(
        error?.response?.data?.response || "Failed to save screen data",
      );
    } finally {
      setSubmitLoading(false);
    }
  };
  const handleEdit = (item) => {
    setEditId(item.id);
    setScreenName(item.screen_name);
    setIsActive(item.is_active);
  };
  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <DashboardCustomizeIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            {editId ? "Edit Screen Name" : "Create Screen Name"}
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}
          >
            Manage app dashboard screen names
          </Typography>
        </Box>
      </Box>

      {/* Form Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          p: 3,
          mb: 3,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Screen Name"
              variant="outlined"
              size="small"
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            {editId && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#016551",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: "#016551" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: isActive ? "#016551" : "#64748b",
                    }}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            )}
            <Button
              type="submit"
              disabled={submitLoading}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                px: 3,
                py: 1,
                boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                },
                "&:disabled": { background: "#ccc" },
              }}
            >
              {submitLoading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : editId ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
            {editId && (
              <Button
                onClick={() => {
                  setEditId(null);
                  setScreenName("");
                  setIsActive(true);
                }}
                sx={{ color: "#64748b", fontWeight: 600, borderRadius: "8px" }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </form>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Section Header */}
        <Box sx={{ background: "#04343a", p: "12px 20px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>
            List of Screens
          </Typography>
        </Box>

        {screenLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress sx={{ color: "#016551" }} size={28} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              overflowY: "auto",
              maxHeight: "65vh",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {["ID", "Screen Name", "Status", "Actions"].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#fff",
                        background: "#04343a",
                        borderBottom: "none",
                        padding: "12px 16px",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {screenList?.map((screen) => (
                  <TableRow
                    key={screen.id}
                    sx={{ "&:hover": { background: "#f0faf5" } }}
                  >
                    <TableCell sx={{ color: "#64748b", fontSize: "13px" }}>
                      {screen.id}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#016551",
                          fontSize: "14px",
                        }}
                      >
                        {screen.screen_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={screen.is_active ? "Active" : "Non-Active"}
                        size="small"
                        sx={{
                          background: screen.is_active ? "#e8f5e9" : "#f1f5f9",
                          color: screen.is_active ? "#016551" : "#64748b",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(screen)}
                        sx={{ color: "#04343a" }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default CreateScreenName;
